/**
 * A11y label constants — single source of truth for aria-label / aria-hidden
 * on icon-only and interactive elements.
 *
 * Phase C5 (Faz 5): previously many icon buttons across Header,
 * MovieMakerLanding and SoftwareShop lacked aria-label attributes,
 * making the site unusable with screen readers.
 *
 * Each label is shaped as { tr, en } so it can be selected by current
 * site language. Use <a11yLabel> helpers from utils/i18n.js (or pick by
 * language inline) when rendering.
 *
 * Guidelines:
 *   - "X aç" / "Open X" for buttons that trigger an action
 *   - "X kapat" / "Close X" for dismissal
 *   - "X'e git" / "Go to X" for nav
 *   - "Şu an X gösteriliyor" / "X currently shown" for state toggles
 */

export const A11Y_LABEL = {
  // Header (used on desktop + mobile)
  HEADER_TOGGLE_MOBILE_MENU: { tr: 'Mobil menüyü aç/kapat', en: 'Toggle mobile menu' },
  HEADER_CLOSE_MOBILE_MENU:  { tr: 'Mobil menüyü kapat',    en: 'Close mobile menu' },
  HEADER_CART_ICON:          { tr: 'Sepet',                 en: 'Cart' },
  HEADER_CART_BADGE:         { tr: 'Sepetteki ürün sayısı',  en: 'Items in cart' },
  HEADER_PRODUCTS_ICON:      { tr: 'Ürünler',               en: 'Products' },
  HEADER_LOGIN_ICON:         { tr: 'Giriş yap',             en: 'Login' },
  HEADER_USER_ICON:          { tr: 'Hesabım',               en: 'My account' },
  HEADER_CATEGORIES_DROPDOWN: { tr: 'Kategoriler',           en: 'Categories' },
  HEADER_SEARCH:             { tr: 'Ara',                   en: 'Search' },
  HEADER_LANG_TR:            { tr: 'Türkçe',                en: 'Turkish' },
  HEADER_LANG_EN:            { tr: 'İngilizce',             en: 'English' },

  // MovieMakerLanding
  MML_LIGHTBOX_CLOSE:        { tr: 'Işıklı kutuyu kapat',   en: 'Close lightbox' },
  MML_LIGHTBOX_PREV:         { tr: 'Önceki medya',          en: 'Previous media' },
  MML_LIGHTBOX_NEXT:         { tr: 'Sonraki medya',         en: 'Next media' },
  MML_LIGHTBOX_VIDEO:        { tr: 'Önizleme videosu',      en: 'Preview video' },
  MML_LIGHTBOX_IMAGE:        { tr: 'Önizleme görseli',      en: 'Preview image' },
  MML_LANGUAGE_TOGGLE:       { tr: 'Dil değiştir',          en: 'Switch language' },
  MML_TUTORIAL_CLOSE:        { tr: 'Eğitim videosunu kapat', en: 'Close tutorial video' },
  MML_TUTORIAL_VIDEO:        { tr: 'Eğitim videosu',        en: 'Tutorial video' },
  MML_NEWSLETTER_SUBMIT:     { tr: 'Bültene abone ol',      en: 'Subscribe to newsletter' },
  MML_DOWNLOAD_CTA:          { tr: 'Pebdeq\'e katıl',       en: 'Join Pebdeq' },
  MML_GALLERY_CTA:           { tr: 'Galeriyi görüntüle',    en: 'View gallery' },
  MML_ANNOUNCEMENT_BAR:      { tr: 'Duyuru',                en: 'Announcement' },

  // SoftwareShop
  SS_FILTER_OPEN:            { tr: 'Filtreleri aç',         en: 'Open filters' },
  SS_ADD_TO_CART:            { tr: 'Sepete ekle',           en: 'Add to cart' },
  SS_FREE_DOWNLOAD:          { tr: 'Ücretsiz indir',        en: 'Download free' },
  SS_DOWNLOADING:            { tr: 'İndiriliyor',           en: 'Downloading' },
  SS_SIGN_UP_TO_DOWNLOAD:    { tr: 'İndirmek için kayıt ol', en: 'Sign up to download' },
  SS_LANG_TOGGLE:            { tr: 'Dil değiştir',          en: 'Switch language' },

  // Common
  NAV_MAIN:                  { tr: 'Ana navigasyon',        en: 'Main navigation' },
  NAV_FOOTER:                { tr: 'Altbilgi bağlantıları', en: 'Footer links' },
  DIALOG:                    { tr: 'İletişim kutusu',       en: 'Dialog' },
};

/**
 * Resolve an a11y label by language, falling back to EN, then TR, then the key.
 * @param {string} key
 * @param {'tr'|'en'} lang
 */
export function a11yLabel(key, lang = 'en') {
  const entry = A11Y_LABEL[key];
  if (!entry) return key;
  return entry[lang] || entry.en || entry.tr || key;
}

export default A11Y_LABEL;